/**
 * Session bootstrap hook.
 *
 * Why: T3's `useMe()` hits `/api/auth/me` unconditionally when it mounts —
 * fine when the user *is* logged in, but on a cold page load for a logged-out
 * user that would fire a guaranteed-401. This hook gates the query on the
 * presence of an access token so the unauth branch stays network-silent.
 *
 * Flow:
 *   1. On mount, ask `tokenStorage.getAccessToken()` (synchronous localStorage
 *      read).
 *   2. If no token → `state = 'unauthenticated'`. No network call.
 *   3. If token → enable `useMe()`. The HttpClient's 401-refresh-retry
 *      (implemented in T3) handles stale tokens automatically. If refresh
 *      fails, `clearTokens()` fires inside the HttpClient, `useMe()` returns
 *      error, and we surface `state = 'unauthenticated'`.
 *   4. If `useMe()` returns a user → `state = 'authenticated'`, cached user +
 *      token both live on. The cached user in localStorage is refreshed so
 *      later reloads have an up-to-date snapshot.
 *
 * This module does NOT own the 401-refresh-retry behavior — that is T3's
 * HttpClient. All we do here is observe token presence + the `useMe()` result.
 */

import { useEffect } from 'react';
import { useMe } from '@shopista/shared/api/hooks';
import { ApiError, ResponseValidationError } from '@shopista/shared/api';
import type { User } from '@shopista/shared/types';
import type { WebTokenStorage } from './tokenStorage';

export type SessionState =
  | { status: 'hydrating'; user: null }
  | { status: 'authenticated'; user: User }
  // `error-retryable` (T27.2.2.2): a cold `/auth/me` failed *transiently* — a
  // `5xx`, a network throw, or a response-validation error (backend bouncing
  // mid-redeploy). Tokens are KEPT; AuthGuard shows a retry, never a redirect,
  // so a transient outage doesn't durably wipe the native Keystore session.
  | { status: 'error-retryable'; user: null; retry: () => void }
  | { status: 'unauthenticated'; user: null };

export interface UseSessionOptions {
  tokens: WebTokenStorage;
}

/**
 * Observe + manage the persistent session.
 *
 * Call once at the root of the protected tree (AuthGuard). Returns a
 * discriminated union the guard can pattern-match on.
 */
export function useSession({ tokens }: UseSessionOptions): SessionState {
  // Presence of a token decides whether we fire /auth/me at all. Gating the
  // query keeps the logged-out branch network-silent.
  const hasToken = tokens.getAccessToken() !== null;

  const me = useMe({ enabled: hasToken });

  // When `useMe()` lands a fresh user, update the cached copy so a hard
  // reload has up-to-date props (household name, avatar, etc.). We write the
  // raw User — `setSession` serializes with JSON.stringify.
  useEffect(() => {
    if (me.status === 'success' && me.data) {
      // Access token is already present — reuse it (legacy parity).
      const accessToken = tokens.getAccessToken();
      if (accessToken) {
        tokens.setSession({ accessToken, user: me.data });
      }
    }
  }, [me.status, me.data, tokens]);

  // When `useMe()` errors AND there's still a token, the HttpClient's
  // refresh flow already cleared tokens. We surface that here as
  // "unauthenticated". The AuthGuard will redirect to /login.
  if (!hasToken) {
    return { status: 'unauthenticated', user: null };
  }
  if (me.isPending) {
    return { status: 'hydrating', user: null };
  }
  if (me.isError) {
    // Discriminate the cold `/auth/me` failure by status (T27.2.2.2). Branch on
    // `ApiError.status`, NOT a string match: a transient failure (`5xx`, a
    // network throw — surfaced as a non-`ApiError`, or a `ResponseValidationError`)
    // must NOT clear the session, because on native `clearTokens()` durably wipes
    // the Keystore refresh token and forces a re-login that survives relaunch.
    // Only a definitive `4xx` is a genuine rejection → clear + redirect.
    if (isTransientMeError(me.error)) {
      return {
        status: 'error-retryable',
        user: null,
        retry: () => {
          void me.refetch();
        },
      };
    }
    // Genuine rejection (any definitive 4xx). If the client's refresh flow
    // already cleared tokens, clearing again has the same effect; if it didn't
    // (a non-401 4xx that never triggered refresh), clear here so subsequent
    // mounts don't loop.
    tokens.clearTokens();
    return { status: 'unauthenticated', user: null };
  }
  if (me.data) {
    return { status: 'authenticated', user: me.data };
  }
  // `isPending=false`, `isError=false`, `data=undefined` — TanStack should
  // not reach here, but fall through to hydrating rather than redirecting.
  return { status: 'hydrating', user: null };
}

/**
 * Decide whether a cold `/auth/me` error is *transient* (keep the session) or a
 * *genuine* rejection (clear it). T27.2.2.2.
 *
 * Transient — keep tokens:
 *   - `ApiError` with `status >= 500` (backend bouncing mid-redeploy).
 *   - A non-`ApiError` throw (a network failure surfaces as a plain `Error`/
 *     `TypeError`, never an `ApiError` — the request never reached a response).
 *   - `ResponseValidationError` (a backend-shape blip, not an auth rejection).
 *
 * Genuine — clear tokens:
 *   - `ApiError` with a definitive `4xx` status (`400`/`401`/`403`/`404`). A
 *     `401` here means the client's refresh flow already ran and failed.
 */
export function isTransientMeError(error: unknown): boolean {
  if (error instanceof ResponseValidationError) {
    return true;
  }
  if (error instanceof ApiError) {
    return error.status >= 500;
  }
  // A network throw (or any non-API error) never produced a response — treat it
  // as transient rather than logging the user out on a connectivity blip.
  return true;
}

/**
 * Companion useEffect-free helper for tests and imperative flows: given a
 * TokenStorage + a User fetcher, report the initial session state without
 * subscribing to a query.
 *
 * Unused in the happy path — retained as a T8 escape hatch for non-React
 * callers (e.g. migration scripts). Exported so the tree-shaker can drop it.
 */
export function readInitialSession(tokens: WebTokenStorage): SessionState {
  if (tokens.getAccessToken() === null) {
    return { status: 'unauthenticated', user: null };
  }
  const cached = tokens.getCachedUser() as User | null;
  if (cached) {
    return { status: 'authenticated', user: cached };
  }
  return { status: 'hydrating', user: null };
}
